import { useState, useCallback } from "react";
import { WEB3FORMS, SITE } from "../config/site";

/**
 * Shared submission logic for all site forms — no backend involved.
 *
 * Primary: Web3Forms (https://web3forms.com), free tier.
 * Fallback: FormSubmit (https://formsubmit.co), free AJAX endpoint delivering
 * to the same business inbox — used automatically if Web3Forms rejects a
 * genuine browser submission (e.g. its domain-level anti-abuse rules).
 *
 * Both providers receive the customer's email as Reply-To, and both use a
 * hidden honeypot for spam protection. Success is shown only when a provider
 * confirms it accepted the submission.
 */

const FORMSUBMIT_ENDPOINT = `https://formsubmit.co/ajax/${SITE.email}`;

async function postWeb3Forms(subject, fromName, fields) {
  const response = await fetch(WEB3FORMS.endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      access_key: WEB3FORMS.accessKey,
      subject,
      from_name: fromName,
      botcheck: "",
      ...fields,
    }),
  });
  const result = await response.json();
  if (!response.ok || result.success === false) {
    throw new Error(result.message || "Web3Forms rejected the submission");
  }
}

async function postFormSubmit(subject, fields, autoresponse, file, fileFieldName) {
  let response;
  if (file) {
    // Multipart path — required for licence-document attachments.
    const fd = new FormData();
    fd.append("_subject", subject);
    fd.append("_template", "basic");
    fd.append("_captcha", "false");
    fd.append("_honey", "");
    Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
    fd.append("_replyto", fields.replyto || fields.email || "");
    fd.append(fileFieldName || "attachment", file, file.name);
    response = await fetch(FORMSUBMIT_ENDPOINT, {
      method: "POST",
      headers: { Accept: "application/json" },
      body: fd,
    });
  } else {
    response = await fetch(FORMSUBMIT_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        _subject: subject,
        _template: "basic",
        _captcha: "false",
        _honey: "",
        ...fields,
        _replyto: fields.replyto || fields.email || "",
        ...(autoresponse ? { _autoresponse: autoresponse } : {}),
      }),
    });
  }
  const result = await response.json();
  const ok =
    response.ok && (result.success === true || result.success === "true");
  if (!ok) {
    throw new Error(result.message || "FormSubmit rejected the submission");
  }
}

export function useWeb3Form({ subject, fromName }) {
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMessage, setErrorMessage] = useState("");

  const doSubmit = useCallback(
    async (fields, autoresponse, file, fileFieldName) => {
      setStatus("sending");
      setErrorMessage("");
      let web3formsError = null;

      // Submissions carrying a licence document must ALWAYS deliver the
      // attachment — Web3Forms is never allowed to silently accept a driver
      // application without it, so file submissions use FormSubmit only.
      if (!file && WEB3FORMS.accessKey && !autoresponse) {
        try {
          await postWeb3Forms(subject, fromName, fields);
          setStatus("success");
          return true;
        } catch (err) {
          web3formsError = err;
        }
      }

      // FormSubmit — primary when an autoresponse is required, otherwise the fallback.
      try {
        await postFormSubmit(subject, fields, autoresponse, file, fileFieldName);
        setStatus("success");
        return true;
      } catch (fallbackErr) {
        // Last resort: if an autoresponse was requested but FormSubmit failed,
        // still deliver to the owner via Web3Forms (passenger ack not possible there).
        // Never used for file submissions — attachments must not be dropped.
        if (!file && autoresponse && WEB3FORMS.accessKey) {
          try {
            await postWeb3Forms(subject, fromName, fields);
            setStatus("success");
            return true;
          } catch (err) {
            web3formsError = err;
          }
        }
        setStatus("error");
        setErrorMessage(
          `Could not send your request (${(web3formsError || fallbackErr).message}). Please email us directly at ${SITE.email}.`
        );
        return false;
      }
    },
    [subject, fromName]
  );

  // `autoresponse` (optional): plain-text acknowledgement emailed to the
  // submitter. Only FormSubmit supports this on the free tier, so when an
  // autoresponse is requested, FormSubmit is tried FIRST — the passenger
  // acknowledgement is never silently lost to a provider that cannot send it.
  const submit = useCallback(
    (fields, autoresponse, file, fileFieldName) => {
      // Overall timeout so a stalled provider never leaves the form stuck on
      // "Sending…" — the user always ends with success or a clear error.
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("the request timed out")), 20000)
      );
      return Promise.race([doSubmit(fields, autoresponse, file, fileFieldName), timeout]).catch(
        (err) => {
          setStatus("error");
          setErrorMessage(
            `Could not send your request (${err.message}). Please email us directly at ${SITE.email}.`
          );
          return false;
        }
      );
    },
    [doSubmit]
  );

  return { status, errorMessage, submit };
}
